import React from 'react';
import { motion } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';

interface HealthScoreGaugeProps {
  score: number | null | undefined;
  isLoading?: boolean;
}

function scoreColor(score: number): string {
  if (score >= 80) return '#27AE60';
  if (score >= 50) return '#F1C40F';
  if (score >= 30) return '#E67E22';
  return '#E74C3C';
}

export const HealthScoreGauge: React.FC<HealthScoreGaugeProps> = ({ score, isLoading }) => {
  if (isLoading) {
    return <Skeleton className="w-[120px] h-[120px] rounded-full mx-auto" />;
  }

  const s = score ?? 0;
  const color = scoreColor(s);
  const size = 120;
  const cx = size / 2;
  const cy = size / 2;
  const r = 46;
  const strokeWidth = 8;

  // Arc from 135° to 405° (270° sweep)
  const startAngle = 135;
  const totalSweep = 270;
  const endAngle = startAngle + (s / 100) * totalSweep;

  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const arcPath = (start: number, end: number) => {
    const x1 = cx + r * Math.cos(toRad(start));
    const y1 = cy + r * Math.sin(toRad(start));
    const x2 = cx + r * Math.cos(toRad(end));
    const y2 = cy + r * Math.sin(toRad(end));
    const largeArc = end - start > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const bgPath = arcPath(startAngle, startAngle + totalSweep);
  const fgPath = s > 0 ? arcPath(startAngle, endAngle) : '';

  return (
    <div className="flex flex-col items-center" style={{ width: size, height: size + 20 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background track */}
        <path
          d={bgPath}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Animated foreground */}
        {s > 0 && (
          <motion.path
            d={fgPath}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          />
        )}
        {/* Score number */}
        <text
          x={cx}
          y={cy - 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={28}
          fontWeight={700}
          fill={color}
        >
          {s}
        </text>
        {/* /100 label */}
        <text
          x={cx}
          y={cy + 18}
          textAnchor="middle"
          fontSize={10}
          fill="hsl(var(--muted-foreground))"
        >
          / 100
        </text>
      </svg>
      <span className="text-xs text-muted-foreground -mt-2">Portfolio Health</span>
    </div>
  );
};
